import { logger } from '@springtree/eva-sdk-core-logger';
import { IEvaServiceCallOptions, bootstrapEndpoint } from '@springtree/eva-sdk-core-service';
import { Core, IEvaServiceDefinition } from '@springtree/eva-services-core';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { createLogic } from 'redux-logic';
import { IEvaReduxStoreDefaultShape } from '../store';
import {
  IEvaServiceErrorActionPayload,
  IEvaServiceRequestAction,
  IEvaServiceRequestActionPayload,
  IEvaServiceResponseActionPayload,
  IStateOptions,
} from '../action';
import { getServicePromise } from '../promise';
import { getStoreSettings } from '../settings';

/**
 * The product search request for the composite helper.
 * You can specify the availability request but the product ids will be overridden
 * from the search response
 *
 * @export
 * @interface IExtendedSearchProductsRequest
 */
export interface IExtendedSearchProductsRequest {
  search?: EVA.Core.SearchProducts;
  availability?: Partial<EVA.Core.GetProductAvailability>;
}

/**
 * The product search response with the product availability for any
 * products returned in the response.
 * If no products are found in the response the availability will not be present
 *
 * @export
 * @interface IExtendedSearchProductsResponse
 */
export interface IExtendedSearchProductsResponse {
  searchResult?: EVA.Core.SearchProductsResponse;
  availability?: EVA.Core.GetProductAvailabilityResponse;
  prices?: EVA.Core.GetProductPricesResponse;
}

/**
 * The composite service definition for the ExtendedSearchProducts helper
 *
 * @export
 * @class ExtendedSearchProducts
 * @implements {IEvaServiceDefinition}
 */
export class ExtendedSearchProducts implements IEvaServiceDefinition {
  name = 'Composite:ExtendedSearchProducts';
  path = '/api/composite/ExtendedSearchProducts';
  request?: IExtendedSearchProductsRequest;
  response?: IExtendedSearchProductsResponse;
}

/**
 * Additional options for the logic handling. Can be used to disable
 * additional request for prices and availability.
 *
 * @export
 * @interface IExtendedSearchProductsLogicOptions
 * @extends {IStateOptions}
 */
export interface IExtendedSearchProductsLogicOptions extends IStateOptions {
  /**
   * Product prices are fetched by default but can be disabled using this options
   *
   * @type {boolean}
   */
  disableProductPrices?: boolean;
}

/**
 * Composite service request logic builder for the ExtendedSearchProducts helper
 *
 * @export
 * @template SHAPE
 * @param {IExtendedSearchProductsLogicOptions} [stateOptions]
 * @returns
 */
export function createExtendedSearchProductsLogic<SHAPE extends IEvaReduxStoreDefaultShape, SVC extends IEvaServiceDefinition = ExtendedSearchProducts>(
  stateOptions?: IExtendedSearchProductsLogicOptions,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const serviceName = (stateOptions && stateOptions.stateKey) || new ExtendedSearchProducts().name;

  const requestLogic = createLogic<SHAPE, IEvaServiceRequestAction<SVC>, any, any, any, string>({
    type: `${serviceName}:REQUEST`,
    processOptions: {
      dispatchReturn: true,
      successType: `${serviceName}:RESPONSE`,
      failType: `${serviceName}:ERROR`,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const settings = getStoreSettings();
      const payload: IEvaServiceRequestActionPayload<ExtendedSearchProducts> = action.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      if (!payload.data) {
        payload.data = {};
      }

      // Bootstrapping the store to get the EvaEndpoint instance we need
      // The SDK caches bootstrap results so after the first call this will be instant
      //
      const endpoint = await bootstrapEndpoint({ uri: endpointUrl });

      // Set provided chain context header
      //
      const serviceNameForSearchProducts = new Core.SearchProducts().name;
      const serviceOptions: IEvaServiceCallOptions = Object.assign(
        {
          chainContext: payload.chainContext,
        },
        payload.serviceOptions,
      );

      // Set the current user authentication token if needed
      //
      const currentUserToken = storeState.currentUserToken;
      if (!serviceOptions.authenticationToken && currentUserToken) {
        serviceOptions.authenticationToken = currentUserToken;
      }

      // Set the current application token if needed
      //
      const currentApplicationToken = storeState.currentApplicationToken;
      if (!serviceOptions.applicationToken && currentApplicationToken) {
        serviceOptions.applicationToken = currentApplicationToken;
      }

      // Call the service and return the response so the reducers can
      // write it to the store state
      //
      logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForSearchProducts}`, payload, serviceOptions)();

      try {
        const searchResult = await endpoint.callService(
          Core.SearchProducts,
          {
            IncludedFields: settings.defaultProductProperties,
            ...payload.data.search,
          },
          serviceOptions,
        );

        // Extract the product identifiers from the response and fetch their availability and prices
        //
        let availability: EVA.Core.GetProductAvailabilityResponse|undefined = undefined;
        let prices: EVA.Core.GetProductPricesResponse|undefined = undefined;
        const searchProducts = searchResult?.Products ?? [];
        const productIds: number[] = searchProducts.map(product => product?.product_id).filter(productId => !!productId);
        if (productIds.length) {
          const organizationUnitsForPickup: number[] = [];
          const currentUser = storeState.currentUser?.data.response;
          if (currentUser?.User?.CurrentOrganizationID) {
            organizationUnitsForPickup.push(currentUser.User.CurrentOrganizationID);
          }

          const requestPayload: EVA.Core.GetProductAvailability = {
            Options: {
              Bundles: {
                IncludeBundleInformation: true,
              },
              Delivery: {
                AvailabilityDate: true,
              },
              Pickup: {
                AvailabilityDate: true,
                OrganizationUnitIDs: organizationUnitsForPickup,
              },
              QuantityAvailable: true,
              ProductAvailabilityDetails: {
                OrganizationUnitIDs: organizationUnitsForPickup,
                ProductIDs: productIds,
              },
            },
            ...payload.data.availability,

            // Override the product ids
            //
            Products: productIds.map((productId) => { return { ID: productId, QuantityRequested: 1 }; }),
          };

          // Set provided chain context header
          //
          const serviceNameForProductAvailability = new Core.GetProductAvailability().name;
          const serviceOptionsForProductAvailability: IEvaServiceCallOptions = Object.assign(
            {
              chainContext: payload.chainContext,
            },
            serviceOptions,
          );

          logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductAvailability}`, requestPayload, serviceOptions)();
          try {
            availability = await endpoint.callService(
              Core.GetProductAvailability,
              requestPayload,
              serviceOptionsForProductAvailability,
            );
          } catch (error) {
            logger.warn(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductAvailability} failed`, error)();
          }

          if (!stateOptions?.disableProductPrices) {
            const requestPayload: EVA.Core.GetProductPrices = {
              ProductIDs: productIds,
            };

            // Set provided chain context header
            //
            const serviceNameForProductPrices = new Core.GetProductPrices().name;
            const serviceOptionsForProductPrices: IEvaServiceCallOptions = Object.assign(
              {
                chainContext: payload.chainContext,
              },
              serviceOptions,
            );

            logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductPrices}`, requestPayload, serviceOptions)();
            try {
              prices = await endpoint.callService(
                Core.GetProductPrices,
                requestPayload,
                serviceOptionsForProductPrices,
              );
            } catch (error) {
              logger.warn(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductPrices} failed`, error)();
            }
          }
        }

        const responsePayload: IEvaServiceResponseActionPayload<ExtendedSearchProducts> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: {
            searchResult,
            availability,
            prices,
          },
        };

        return responsePayload;
      } catch (error) {
        logger.error(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Service error`, error)();

        const errorPayload: IEvaServiceErrorActionPayload<ExtendedSearchProducts> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: error.message,
        };

        // If this an HTTPError from fetch we can try to extract the JSON response
        //
        if (error instanceof ky.HTTPError) {
          try {
            const httpError: ky.HTTPError = error;
            const jsonError = await httpError.response.json();
            errorPayload.data = jsonError || error.message;
            errorPayload.httpResponse = error.response.status;
            const headers: Record<string, string> = {};
            error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
            errorPayload.headers = headers;
          } catch (jsonParseError) {
            logger.warn(`[EVA:SDK:LOGIC:${serviceName}] Failed to parse JSON error response`, jsonParseError)();
          }
        }

        servicePromise.reject(errorPayload);
        throw errorPayload;
      }
    },
  });

  return [
    requestLogic,
  ];
}
